import type { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import type {
  MutationDefinition,
  MutationResultSelectorResult,
  QueryArgFrom,
  ResultTypeFrom,
} from '@reduxjs/toolkit/query';
import type { MutationTrigger, UseMutationStateResult } from '@reduxjs/toolkit/src/query/react/buildHooks';
import { useCallback, useEffect } from 'react';

import { useStoreDispatch } from '@hook/store/store.hooks';
import type { BaseStoreInterface } from '@interface/store/base-store.interface';
import { UNLOADED_STATUS } from '@util/constants/api.constants';
import { isExist } from '@util/data/is-exist';

interface ReducerActionsInterface {
  startRequest: ActionCreatorWithoutPayload;
  setLoaded: ActionCreatorWithPayload<boolean>;
  endRequest: ActionCreatorWithPayload<Pick<BaseStoreInterface, 'error'>>;
}

export const useMutation = <
  RT = any,
  D extends MutationDefinition<any, any, any, RT> = MutationDefinition<any, any, any, RT>,
  R extends Record<string, any> = MutationResultSelectorResult<D>,
>(
  mutation: readonly [MutationTrigger<D>, UseMutationStateResult<D, R>],
  actions: ReducerActionsInterface,
  setData?: ActionCreatorWithPayload<ResultTypeFrom<D>>,
) => {
  const dispatch = useStoreDispatch();
  const [call, state] = mutation;

  useEffect(
    () => void (!UNLOADED_STATUS.has(state.status) && dispatch(actions.endRequest({ error: state.error ?? null }))),
    [state.status],
  );

  useEffect(
    () => void (isExist(state.data) && isExist(setData) && dispatch(setData(state.data))),
    [state.data, state.error, setData],
  );

  useEffect(() => void dispatch(actions.setLoaded(!UNLOADED_STATUS.has(state.status))), [state.status]);

  return {
    action: useCallback(
      (data: QueryArgFrom<D>) => {
        dispatch(actions.startRequest());

        return call(data).unwrap();
      },
      [dispatch, call, actions.startRequest],
    ),
    state,
  };
};
